import * as React from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { t } from "@impero-com/common/utils/i18n";
import { Button, Svg } from "@impero-com/ui";
import Remove from "@impero-com/ui/img/remove.svg";
import { Typography } from "antd";
import Table, { ColumnProps } from "antd/es/table";
import memoizeOne from "memoize-one";

import {
    ControlOverviewListDto,
    MitigatingControl,
    UpdatedMitigatingControl,
    ControlOverview,
    ControlProgramOverview,
} from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { NumericInput } from "components/form/legacyFields/numericInput";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import * as T from "org/risks/content/types";
import { Guid } from "utils/guid";

import { WebRoute } from "../../../../routes";

import { AssignControl } from "./assignControl";
import * as theme from "./theme.css";

const { Text } = Typography;

class MitigatingControls extends React.PureComponent<
    MitigatingControlsProps,
    MitigatingControlsState
> {
    state: MitigatingControlsState = {
        create: false,
    };

    private rows = memoizeOne(
        (
            mitigatingControls: MitigatingControl[],
            controlIdToControlAndActions: Record<Guid, T.ControlAndActions>
        ): Row[] => {
            const stringCompare = Cmp.string();
            return mitigatingControls
                .map((mitigatingControl) => {
                    const { controlId, riskCoverage, historicControl } =
                        mitigatingControl;

                    let control: ControlOverview;
                    let controlProgram: ControlProgramOverview;
                    let controlAction: T.ActionType;
                    let controlProgramAction: T.ActionType;
                    if (
                        historicControl !== null &&
                        historicControl !== undefined
                    ) {
                        // This is the history view, so we display the variables as it was when persisted
                        control = {
                            id: controlId,
                            title: historicControl.title,
                            controlProgramId: historicControl.controlProgramId,
                            description: null,
                        };

                        let running = false;
                        // Control or control program might have been deleted since
                        const liveControlAndActions =
                            controlIdToControlAndActions[controlId];
                        if (liveControlAndActions) {
                            controlAction = liveControlAndActions.controlAction;
                            controlProgramAction =
                                liveControlAndActions.controlProgramAction;
                            running =
                                liveControlAndActions.controlProgram.running;
                        } else {
                            controlAction = null;
                            controlProgramAction = null;
                        }

                        controlProgram = {
                            id: historicControl.controlProgramId,
                            name: historicControl.controlProgramName,
                            description: null,
                            running,
                            responsibleId: null,
                        };
                    } else {
                        // In live version the backend guarantee all controls are in the controlIdToControlAndActions map
                        const controlAndAction =
                            controlIdToControlAndActions[controlId];
                        control = controlAndAction.control;
                        controlProgram = controlAndAction.controlProgram;
                        controlAction = controlAndAction.controlAction;
                        controlProgramAction =
                            controlAndAction.controlProgramAction;
                    }

                    return {
                        key: control.id,
                        control,
                        controlProgram,
                        riskCoverage: riskCoverage ?? undefined,
                        canViewControl: controlAction !== null,
                        canViewControlProgram: controlProgramAction !== null,
                    };
                })
                .sort(
                    Cmp.chain([
                        Cmp.map(
                            (row) => row.controlProgram.name,
                            stringCompare
                        ),
                        Cmp.map((row) => row.control.title, stringCompare),
                    ])
                );
        }
    );
    private unassignedControls = memoizeOne(
        (
            mitigatingControls: MitigatingControl[] | undefined,
            controls: ControlOverview[]
        ) => {
            if (mitigatingControls === undefined) {
                return controls;
            }

            return controls.filter(
                (control) =>
                    mitigatingControls.findIndex(
                        (mitigatingControl) =>
                            mitigatingControl.controlId === control.id
                    ) < 0
            );
        }
    );

    render = () => {
        const {
            mitigatingControls,
            allControls,
            type,
            controlIdToControlAndActions,
            error,
        } = this.props;

        return (
            <BaseFormDisplay
                label={t("RiskDirectory_MitigatingControls_MitigatingActions")}
                error={error}
            >
                {mitigatingControls === undefined ||
                mitigatingControls.length === 0 ? (
                    <div>
                        <Text type="secondary">
                            {t(
                                "RiskDirectory_MitigatingControls_NoMitigatingActions"
                            )}
                        </Text>
                    </div>
                ) : (
                    <Table
                        dataSource={this.rows(
                            mitigatingControls,
                            controlIdToControlAndActions
                        )}
                        columns={
                            type === "EDIT"
                                ? this.editColumns()
                                : this.readOnlyColumns()
                        }
                        pagination={false}
                        bordered
                    />
                )}
                {type === "EDIT" &&
                this.unassignedControls(
                    mitigatingControls,
                    allControls.content.map((dto) => dto.resource)
                ).length > 0 ? (
                    <Button
                        variant="link"
                        id="assign-control"
                        onClick={() => this.setState({ create: true })}
                        className={theme.noPaddingButton}
                    >
                        {t("RiskMapResponse_AddControlButton")}
                    </Button>
                ) : null}
                {this.renderSubDrawer()}
            </BaseFormDisplay>
        );
    };

    renderSubDrawer = () => {
        const {
            type,
            mitigatingControls,
            allControls,
            controlIdToControlAndActions,
        } = this.props;
        const { create } = this.state;

        if (type !== "EDIT" || !create) {
            return null;
        }

        return (
            <AssignControl
                onCancel={() => this.setState({ create: false })}
                save={(mitigatingControl) =>
                    this.createMitigatingControl(mitigatingControl).then(() =>
                        this.setState({ create: false })
                    )
                }
                unassignedControls={this.unassignedControls(
                    mitigatingControls,
                    allControls.content.map((dto) => dto.resource)
                )}
                controlIdToControlAndActions={controlIdToControlAndActions}
            />
        );
    };

    readOnlyColumns = (): ColumnProps<Row>[] => {
        return [
            this.programControlColumn(),
            {
                key: "coverage",
                title: t("RiskMapResponse_Coverage"),
                dataIndex: "riskCoverage",
                width: 100,
            },
        ];
    };

    editColumns = (): ColumnProps<Row>[] => {
        return [
            this.programControlColumn(),
            {
                key: "coverage",
                title: t("RiskMapResponse_Coverage"),
                dataIndex: "riskCoverage",
                render: (_text, record, _index) =>
                    record.canViewControl ? (
                        <NumericInput
                            id={`coverage-${record.control.id}`}
                            type="EDIT"
                            label={t("RiskMapResponse_Coverage")}
                            onChange={(value) =>
                                this.updateCoverage(record.control.id, value)
                            }
                            value={record.riskCoverage ?? 0}
                            validators={[
                                { kind: "ruleset", type: "integer", min: 0 },
                            ]}
                            formatter={(value) => `${value}%`}
                            parser={(value) => (value ?? "").replace("%", "")}
                            lite
                        />
                    ) : (
                        `${record.riskCoverage ?? 0}%`
                    ),
                width: 100,
            },
            {
                key: "delete",
                render: (_text, record, _index) => (
                    <Button
                        id={`delete-${record.control.id}`}
                        variant="link"
                        onClick={() => {
                            this.deleteMitigatingControl(record.control.id);
                        }}
                        className={theme.mitigatingControlsDelete}
                        disabled={!record.canViewControl}
                    >
                        <Svg src={Remove} />
                    </Button>
                ),
                width: 50,
            },
        ];
    };

    programControlColumn = (): ColumnProps<Row> => {
        return {
            key: "program-control",
            title: t("RiskDirectory_MitigatingControls_Column_ProgramControl"),
            render: (_text, record, _index) => (
                <div>
                    <div>
                        {record.canViewControl ? (
                            <a
                                href={`${WebRoute.CONTROLS}/${record.control.id}`}
                                target="_blank"
                            >
                                {record.control.title}
                            </a>
                        ) : (
                            record.control.title
                        )}
                    </div>
                    <div className={theme.controlProgramContainer}>
                        <span className={theme.inControlProgram}>
                            {t("In")}
                        </span>{" "}
                        {record.canViewControlProgram ? (
                            <a
                                className={theme.controlProgramLink}
                                href={buildControlProgramViewUrl(
                                    record.controlProgram.id
                                )}
                                target="_blank"
                            >
                                {record.controlProgram.name}
                            </a>
                        ) : (
                            record.controlProgram.name
                        )}
                    </div>
                </div>
            ),
        };
    };

    updateCoverage = (
        controlId: Guid,
        riskCoverage: number | undefined
    ): Promise<void> => {
        if (this.props.type !== "EDIT") {
            throw new Error(
                `Cannot update coverage with type "${this.props.type}", "EDIT" expected`
            );
        }
        return this.props.onUpdate((mitigatingControls) => {
            const index = mitigatingControls?.findIndex(
                (mitigatingControl) => mitigatingControl.controlId === controlId
            );
            if (
                mitigatingControls === undefined ||
                index === undefined /* mitigatingControls is undefined */ ||
                index === -1 /* controlId not found */ ||
                mitigatingControls[index].riskCoverage ===
                    riskCoverage /* unchanged */
            ) {
                // Nothing to do
                return undefined;
            }

            const newMitigatingControls = mitigatingControls.slice();
            newMitigatingControls[index] = {
                controlId,
                riskCoverage: riskCoverage ?? null,
            };
            return newMitigatingControls;
        });
    };

    deleteMitigatingControl = (controlId: Guid): Promise<void> => {
        if (this.props.type !== "EDIT") {
            throw new Error(
                `Cannot delete mitigating control with type "${this.props.type}", "EDIT" expected`
            );
        }
        return this.props.onUpdate((mitigatingControls) => {
            const index = mitigatingControls?.findIndex(
                (mitigatingControl) => mitigatingControl.controlId === controlId
            );
            if (
                mitigatingControls === undefined ||
                index === undefined /* mitigatingControls is undefined */ ||
                index === -1 /* controlId not found */
            ) {
                // Nothing to do
                return undefined;
            }

            return [
                ...mitigatingControls.slice(0, index),
                ...mitigatingControls.slice(index + 1),
            ];
        });
    };

    createMitigatingControl = (
        mitigatingControl: MitigatingControl
    ): Promise<void> => {
        if (this.props.type !== "EDIT") {
            throw new Error(
                `Cannot delete mitigating control with type "${this.props.type}", "EDIT" expected`
            );
        }
        return this.props.onUpdate((mitigatingControls) => {
            const index = mitigatingControls?.findIndex(
                (mc) => mc.controlId === mitigatingControl.controlId
            );
            if (index !== undefined && index >= 0 /* Already present */) {
                // Nothing to do
                return undefined;
            }
            return [...(mitigatingControls ?? []), mitigatingControl];
        });
    };
}

interface MitigatingControlsCommonProps {
    mitigatingControls: Array<MitigatingControl> | undefined;
    allControls: ControlOverviewListDto;
    controlIdToControlAndActions: Record<Guid, T.ControlAndActions>;
    error?: string;
}

type MitigatingControlsProps = (
    | { type: "READ_ONLY" }
    | {
          type: "EDIT";
          onUpdate: (
              callback: (
                  mitigatingControls:
                      | Array<UpdatedMitigatingControl>
                      | undefined
              ) => Array<UpdatedMitigatingControl> | undefined
          ) => Promise<void>;
      }
) &
    MitigatingControlsCommonProps;

interface MitigatingControlsState {
    create: boolean;
}

interface Row {
    key: string;
    control: ControlOverview;
    controlProgram: ControlProgramOverview;
    riskCoverage: number | undefined;
    canViewControl: boolean;
    canViewControlProgram: boolean;
}

export {
    MitigatingControls,
    type MitigatingControlsProps,
    type MitigatingControlsState,
};
